// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Nextmine works in any browser, but it is at its best living on your phone like any other app: one tap from your home screen, full screen, and able to send you a short notification the moment something happens with your pieces. A buyer messages you, someone makes an offer, your order ships. The whole guide below takes about a minute to follow.

One thing worth knowing before we start: on iPhone, notifications only work when Nextmine is opened from your home screen. That is Apple's rule for every website, not ours. So on iPhone the home screen step comes first, and then notifications are one tap away.

## How do I add Nextmine to my iPhone home screen?

1. Open [nextminejewelry.com](https://nextminejewelry.com) in **Safari**. It has to be Safari for this step, not Chrome or an in-app browser.
2. Tap the **Share** button, the square with the arrow pointing up, at the bottom of the screen.
3. Scroll down the list and tap **Add to Home Screen**.
4. Tap **Add** in the top corner.

That is it. Nextmine now sits on your home screen with its own icon, and from now on you open it from there. It looks and feels like an app, opens full screen without the browser bars, and keeps you signed in.

You need iOS 16.4 or newer for notifications, which covers every iPhone from the last several years.

## How do I add Nextmine to my Android home screen?

1. Open [nextminejewelry.com](https://nextminejewelry.com) in **Chrome**.
2. Tap the **three dots** in the top corner.
3. Tap **Add to Home screen** (on some phones it says **Install app**).
4. Confirm with **Add** or **Install**.

On Android you do not strictly need the home screen for notifications, Chrome can send them from a normal tab too, but the home screen app is still the nicer way to use Nextmine day to day.

## How do I turn on notifications?

Once you are signed in, Nextmine will ask you at the natural moments: there is a small **Get notified** card at the top of your inbox, on your listings page, and on every order and conversation. Tap **Turn on** and confirm when your phone asks for permission. Done.

If you would rather do it yourself, go to **Manage account** and use the **Push notifications** section there. The same place lets you turn them off again for the device you are holding, any time.

There is a small thank you waiting as well: a discount code for 10% off your next purchase arrives as your very first notification, the moment you turn them on. It works once per person, and the discount is on us - the seller still receives their full amount.

On iPhone, remember: open Nextmine from the home screen icon first. If you turn notifications on inside plain Safari, the phone has no app to deliver them to, so the option only appears in the home screen version.

Notifications work on computers too. If you spend your day at a desk, turn them on in Chrome, Edge or Firefox and Nextmine will ping you there.

## What will Nextmine actually notify me about?

Only things that concern you directly:

- A buyer places an order on one of your pieces
- Someone makes you an offer, or answers yours
- Your offer is accepted
- Your order is marked as shipped or delivered
- Someone sends you a message
- A shipping quote arrives on your order

That is the whole list. No marketing, no daily digests, no "we miss you". If nothing happens with your pieces, your phone stays quiet.

For a seller this matters more than it might sound. Buyers write to several sellers at once, and the one who answers first is usually the one who sells. A notification turns a question asked at lunch into a sale closed by dinner, instead of a message discovered three days later. If you are serious about selling, this plus [an honest, complete listing](/blog/how-to-write-a-jewelry-listing-that-sells) is most of the game.

## How do I turn notifications off again?

Open Nextmine, go to **Manage account**, find **Push notifications** and tap **Turn off on this device**. You can also silence them from your phone's own settings, under Notifications, like any other app. Turning them off does not affect your account or your listings, and email notifications continue as normal either way.

If you enabled notifications on several devices, each one is managed separately, so you can keep them on your phone and off your laptop.
`;

export default body;
